import React, { memo, useCallback } from "react";
import { SimpleSelect, SimpleSelectProps } from "../../SimpleSelect";
import { UNITS } from "../constants";
import { Locale, PeriodType } from "../types";
import { useCronFieldProps } from "./utils";

export type WeekDaysProps = Omit<SimpleSelectProps<number>, "onChange" | "options"> & {
  humanizeLabels?: boolean
  preferShortHumanLabels?: boolean
  locale: Locale
  onChange?: ((newValue: number[]) => void) | undefined
  monthDays?: number[] | null
  period: PeriodType
};

export const WeekDays = memo((
  {
    locale,
    humanizeLabels,
    preferShortHumanLabels,
    monthDays,
    period,
    ...props
  }: WeekDaysProps) => {
  const value = props.value as number[];
  const noMonthDays = period === "week" || !monthDays || monthDays.length === 0;

  const displayWeekDays =
    period === "week" ||
    (value && value.length > 0) ||
    ((!value || value.length === 0) && (!monthDays || monthDays.length === 0));

  const monthDaysIsDisplayed =
    (monthDays && monthDays.length > 0) ||
    ((!monthDays || monthDays.length === 0) && (!value || value.length === 0));

  const { options, onChange, isMulti } = useCronFieldProps(
    props.value,
    props.onChange,
    props.isMulti ?? false,
    UNITS.find(x => x.type === "week-days")!,
  );

  const setOptionLabel = useCallback((v: number) => {
    if (!humanizeLabels) {
      return v.toString();
    }
    return preferShortHumanLabels ? locale?.altWeekDays?.[v] : locale?.weekDays?.[v];
  }, [ humanizeLabels, preferShortHumanLabels, locale ]);


  return displayWeekDays ? (
    <div>
      {(locale.prefixWeekDays !== "" && (period === "week" || !monthDaysIsDisplayed)) && (
        <span>
          {locale.prefixWeekDays}
        </span>
      )}

      {(locale.prefixWeekDaysForMonthAndYearPeriod !== "" && period !== "week" && monthDaysIsDisplayed) && (
        <span>
          {locale.prefixWeekDaysForMonthAndYearPeriod}
        </span>
      )}

      <SimpleSelect
        placeholder={noMonthDays ? locale.emptyWeekDays : locale.emptyWeekDaysShort}
        isClearable
        isSearchable
        {...props}
        isMulti={isMulti}
        onChange={onChange}
        options={options}
        setOptionLabel={setOptionLabel}
      />
    </div>
  ) : null;
});
